import * as React from 'react';
import {Link as RouterLink} from 'react-router-dom';
import {isAbsolute, isNonHttpProtocol, stripHost} from '@web-utilities/url';
import {isSafe} from '@shopify/safe-redirect';

export interface Props extends React.HTMLProps<HTMLAnchorElement> {
  external?: boolean;
  url: string;
}

/** Cleans up malformed urls (eg. containing spaces) */
function sanitizeUrl(url: string) {
  try {
    return new URL(url).href;
  } catch (_) {
    // ignoring error to let isSafe to its job
    return url;
  }
}

export function Link({ref, url: urlProp, external, children, ...rest}: Props) {
  const url = sanitizeUrl(urlProp);

  // If the URL scheme is javascript:, we want to return it as plain text to prevent XSS
  if (!isSafe(url)) {
    return <a {...rest}>{children}</a>;
  }

  // We don't want to use a React Router link when we are dealing with
  // a non-HTTP url.
  if (isNonHttpProtocol(url)) {
    return (
      <a href={url} {...rest}>
        {children}
      </a>
    );
  }

  if (!isAbsolute(url)) {
    return (
      <RouterLink to={stripHost(url)} {...rest}>
        {children}
      </RouterLink>
    );
  }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer" {...rest}>
      {children}
    </a>
  );
}
